// Lo sfondo del login e dell'aggiornamento (tela Claude Design, artboard 56): la piazza di
// PopTown dove i pop vivono da soli (town/sim.ts) su una città vuota. Canvas a schermo intero,
// inquadratura «a copertura»; le mete stanno nella parte visibile e fuori dalla card (`avoid`).
// Ferma (`motion = false`, o «riduci movimento»): la città vive qualche secondo senza disegno e
// poi resta su quel momento. Con la scheda del browser nascosta non gira.
import { useEffect, useRef } from 'react';
import { CAST } from './town/cast';
import { drawFrame, loadSprite, rigFor, type Sprite, type View } from './town/draw';
import { Town } from './town/sim';
import { WORLD, buildGrid } from './town/walk';
import './town.css';

const AVOID = '.login-card, .town-avoid';

function population(width: number): number {
  return width >= 1100 ? CAST.length : width >= 700 ? 10 : 6;
}

/** La città nella misura che serve: 4K solo dove lo schermo ha più di 1920 pixel veri di
 * larghezza (grande o retina), così un telefono non scarica 900 KB. */
function groundFor(width: number, dpr: number): string {
  if (width <= 700) return '/town/poptown-town-small.webp';
  return width * Math.min(2, dpr) > 1920 ? '/town/poptown-town-4k.webp' : '/town/poptown-town.webp';
}

export function TownScene({ motion = true }: { motion?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const live = useRef(motion);
  live.current = motion;

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext('2d');
    if (!el || !ctx) return undefined;
    let stop = false;
    let frame = 0;
    const grid = buildGrid();
    const town = new Town(grid, population(window.innerWidth));
    // solo in sviluppo: le prove misurano distanze e percorsi (nessun effetto in produzione)
    if (import.meta.env.DEV) Object.assign(window, { __town: town, __grid: grid });
    const sprites = new Map<string, Sprite>();
    const ground = new Image();
    ground.src = groundFor(window.innerWidth, window.devicePixelRatio || 1);
    let view: View = { scale: 1, ox: 0, oy: 0, dpr: 1 };

    const fit = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      el.width = Math.round(window.innerWidth * dpr);
      el.height = Math.round(window.innerHeight * dpr);
      const scale = Math.max(el.width / WORLD.w, el.height / WORLD.h);
      view = {
        scale,
        ox: (el.width - WORLD.w * scale) / 2,
        oy: (el.height - WORLD.h * scale) / 2,
        dpr,
      };
      const toWorld = (x: number, y: number) => ({
        x: (x * dpr - view.ox) / scale,
        y: (y * dpr - view.oy) / scale,
      });
      const a = toWorld(0, 0);
      const b = toWorld(window.innerWidth, window.innerHeight);
      town.view = { x0: a.x, y0: a.y, x1: b.x, y1: b.y };
      const card = document.querySelector(AVOID)?.getBoundingClientRect();
      if (card) {
        const c0 = toWorld(card.left, card.top);
        const c1 = toWorld(card.right, card.bottom);
        town.setCard({ x0: c0.x, y0: c0.y, x1: c1.x, y1: c1.y });
      }
    };

    let last = performance.now();
    const tick = (now: number) => {
      if (stop) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (live.current && !document.hidden) {
        town.step(dt);
        drawFrame(ctx, ground, sprites, town, view, now / 1000);
      }
      frame = requestAnimationFrame(tick);
    };

    void (async () => {
      await Promise.all([
        ground.decode(),
        ...town.pops.map(async (p) => {
          const rig = rigFor(p.role, p.variant);
          if (!sprites.has(rig.id)) sprites.set(rig.id, await loadSprite(rig));
        }),
      ]).catch(() => undefined);
      if (stop) return;
      fit();
      // la città vive un po' prima di farsi vedere: nessuno è fermo dove è nato
      for (let i = 0; i < 160; i++) town.step(0.05);
      drawFrame(ctx, ground, sprites, town, view, 0);
      last = performance.now();
      frame = requestAnimationFrame(tick);
    })();

    const onResize = () => {
      fit();
      drawFrame(ctx, ground, sprites, town, view, performance.now() / 1000);
    };
    window.addEventListener('resize', onResize);
    return () => {
      stop = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="town" aria-hidden="true">
      <canvas ref={canvas} className="town-canvas" />
      <div className="town-veil" />
    </div>
  );
}
